import { HistoryManager } from './HistoryManager';
// import { parseJPEG, type Marker } from './JpegParser';
import { ParserRegistry } from './ParserRegistry';
import type { FileSegment } from './Parser';

export interface AppStateData {
  buffer: Uint8Array | null;
  originalBuffer: Uint8Array | null;
  filename: string | null;
  markers: FileSegment[];
  metadata?: Record<string, string>;
  format?: string;
  selectedMarkerIndex: number;
  editPage: number;
  isGenerated?: boolean;
}

export class AppState {
  private history: HistoryManager<AppStateData>;
  private readonly listeners: Set<(state: AppStateData) => void> = new Set();

  constructor() {
    this.history = new HistoryManager<AppStateData>(30);
    // Initial empty state
    this.history.push({
      buffer: null,
      originalBuffer: null,
      filename: null,
      markers: [],
      selectedMarkerIndex: -1,
      editPage: 0,
      isGenerated: false, // Initialize isGenerated
    });
  }

  get current(): AppStateData {
    return this.history.getCurrent()!;
  }

  loadFile(buffer: Uint8Array, filename: string, isGenerated: boolean = false) {
    // Reset history on new file
    this.history.reset();
    const parser = ParserRegistry.getParser(filename);
    const result = parser.parse(buffer);

    const newState: AppStateData = {
      buffer: buffer,
      originalBuffer: buffer,
      filename: filename,
      markers: result.segments,
      format: result.format,
      metadata: result.metadata,
      selectedMarkerIndex: -1,
      editPage: 0,
      isGenerated: isGenerated,
    };
    this.history.push(newState);
    this.notify();
  }

  updateBuffer(newBuffer: Uint8Array) {
    const currentState = this.current;
    if (!currentState.buffer) return;

    const parser = ParserRegistry.getParser(currentState.filename);
    // Note: Re-parsing might lose some metadata if it depends on original valid structure
    // But for now we re-parse to get updated markers.
    // We can preserve original metadata if we want, or re-parse.
    // Let's re-parse to see if Exif is corrupted/lost.
    const result = parser.parse(newBuffer);

    const newState: AppStateData = {
      ...currentState,
      buffer: newBuffer,
      markers: result.segments,
      // Update format/metadata too just in case
      format: result.format,
      metadata: result.metadata,
    };
    this.history.push(newState);
    this.notify();
  }

  selectMarker(index: number) {
    if (this.current.selectedMarkerIndex === index) return;

    // Changing selection adds a new history state?
    // Usually selection change is ephemeral and doesn't need Undo/Redo support unless we want to undo selection.
    // The PoC allows Undo/Redo of buffer changes.
    // BUT, let's keep it simple: Selection IS part of state?
    // If I undo a glitch, I probably want to stay on the same selection.
    // Making selection part of the history stack might be annoying if I just click around.

    // Let's modify the CURRENT state for selection without pushing new history?
    // But HistoryManager is strict.
    // For now, let's make selection EPHEMERAL or update the current history tip in place?
    // HistoryManager doesn't support "replace tip".

    // Alternative: Separate ViewState from DataState.
    // Refactoring plan: Keep AppState for DATA (Buffer). ViewState for UI.
    // However, for simplicity, I'll modify the current state object in place for selection if I don't want to support undoing selection.
    // actually, `current` returns reference to object in history array.

    const state = this.current;
    if (state) {
      state.selectedMarkerIndex = index;
      state.editPage = 0; // Reset page on selection change
      this.notify();
    }
  }

  setPage(page: number) {
    const state = this.current;
    if (state) {
      state.editPage = page;
      this.notify();
    }
  }

  undo() {
    if (this.history.undo()) {
      this.notify();
    }
  }

  redo() {
    if (this.history.redo()) {
      this.notify();
    }
  }

  canUndo() { return this.history.canUndo(); }
  canRedo() { return this.history.canRedo(); }

  subscribe(listener: (state: AppStateData) => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify() {
    const state = this.current;
    this.listeners.forEach((l) => l(state));
  }
}
